/**
 * 이벤트 상세 페이지
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { eventService } from '../../services/eventService';
import { Event } from '../../types/event';

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const eventId = id ? parseInt(id, 10) : 0;

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => eventService.getEvent(eventId),
    enabled: !!eventId && eventId > 0,
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">진행중</Badge>;
      case 'ended':
        return <Badge bg="secondary">종료</Badge>;
      default:
        return <Badge bg="warning">비활성</Badge>;
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!event) {
    return (
      <AdminLayout>
        <Card>
          <Card.Body>
            <p>이벤트를 찾을 수 없습니다.</p>
            <Button variant="secondary" onClick={() => navigate('/admin/events')}>
              목록으로
            </Button>
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>이벤트 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/events')} className="me-2">
              목록으로
            </Button>
            <Button variant="primary" onClick={() => navigate(`/admin/events/${eventId}/edit`)}>
              수정
            </Button>
          </div>
        </div>

        <Card>
          <Card.Body>
            <div className="mb-3">
              <h3>{event.event_name}</h3>
              <div className="mt-2">
                <Badge bg="info" className="me-2">{event.event_code}</Badge>
                {getStatusBadge(event.status)}
                {event.event_type && (
                  <Badge bg="secondary" className="ms-2">{event.event_type}</Badge>
                )}
              </div>
            </div>

            {event.image_url && (
              <div className="mb-3">
                <img 
                  src={event.image_url} 
                  alt={event.event_name}
                  style={{ maxWidth: '100%', borderRadius: '5px' }}
                />
              </div>
            )}

            <div className="mb-3">
              <strong>이벤트 기간:</strong> {new Date(event.start_date).toLocaleString('ko-KR')} ~ {new Date(event.end_date).toLocaleString('ko-KR')}
            </div>

            <div className="mb-3">
              <strong>등록일:</strong> {new Date(event.created_at).toLocaleString('ko-KR')}
            </div>

            {event.updated_at && (
              <div className="mb-3">
                <strong>수정일:</strong> {new Date(event.updated_at).toLocaleString('ko-KR')}
              </div>
            )}

            {event.description && (
              <>
                <hr />
                <div className="mb-3">
                  <h5>설명</h5>
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#2E2E3A', 
                    borderRadius: '5px',
                    color: '#ccc',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {event.description}
                  </div>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default EventDetail;

